import { apiFetch } from "./apiClient";
import { refusedWriteError, withPageConnection } from "./loadedConnection";

export interface EffectiveRoomType {
    externalId: string;
    name: string;
    capacity: number;
    extraCapacity: number;
    units?: Array<{ externalId: string; name: string }>;
}

export interface EditorRoomKindRow {
    type: EffectiveRoomType & { inventoryCount?: number };
    hidden: boolean;
    autoHidden: boolean;
    pms: {
        name: string;
        capacity: number;
        extraCapacity: number;
        inventoryCount: number;
    };
}

export interface RoomKindOverride {
    roomKindId: string;
    displayName: string | null;
    inventoryCount: number | null;
    capacity: number | null;
    extraCapacity: number | null;
    hidden: boolean;
    /** 0-based position in the operator's own order; null when never placed. */
    sortOrder?: number | null;
}

export interface RoomKindOverrideInput {
    roomKindId: string;
    displayName?: string | null;
    inventoryCount?: number | null;
    capacity?: number | null;
    extraCapacity?: number | null;
    hidden?: boolean;
    sortOrder?: number | null;
}

/** One room kind, as it appears in the order every calendar draws. */
export interface RoomKindOrderEntry {
    roomKindId: string;
    name: string;
}

export interface RoomKindOverridesResponse {
    overrides: RoomKindOverride[];
    effective: EffectiveRoomType[];
    editorRows?: EditorRoomKindRow[];
}

export async function fetchRoomKindOverrides(): Promise<RoomKindOverridesResponse> {
    const res = await apiFetch("/api/room-kind-overrides");
    if (!res.ok) throw new Error(`fetchRoomKindOverrides failed: ${res.status}`);
    return (await res.json()) as RoomKindOverridesResponse;
}

/**
 * The room kinds in the operator's saved order.
 *
 * Cached for the session because every calendar asks for it on mount and the
 * answer only changes when somebody saves the structure — which clears this.
 */
let orderPromise: Promise<RoomKindOrderEntry[]> | null = null;

export function invalidateRoomKindOrder(): void {
    orderPromise = null;
}

export function fetchRoomKindOrder(): Promise<RoomKindOrderEntry[]> {
    if (!orderPromise) {
        orderPromise = (async () => {
            const res = await apiFetch("/api/room-kind-overrides/order");
            if (!res.ok) throw new Error(`fetchRoomKindOrder failed: ${res.status}`);
            const data = (await res.json()) as { roomKinds?: RoomKindOrderEntry[] };
            return data.roomKinds ?? [];
        })().catch((err) => {
            // A failed request must not become the cached answer — the next
            // grid to mount should get a fresh try.
            orderPromise = null;
            throw err;
        });
    }
    return orderPromise;
}

/**
 * Save the open property's room kind structure. A save hides and reveals room
 * kinds, which prunes and reprices their drafts, and the structure's read
 * names no property: so it names the one the page on screen was loaded for
 * (`withPageConnection`), and is refused with `ActiveConnectionChangedError`
 * once another one is open.
 */
export async function saveRoomKindOverrides(
    overrides: RoomKindOverrideInput[],
): Promise<RoomKindOverridesResponse> {
    const res = await apiFetch(withPageConnection("/api/room-kind-overrides"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ overrides }),
    });
    if (res.status === 409) throw await refusedWriteError(res, "saveRoomKindOverrides failed");
    if (!res.ok) {
        let message = `saveRoomKindOverrides failed: ${res.status}`;
        try {
            const data = (await res.json()) as { message?: string };
            if (data?.message) message = data.message;
        } catch {
            /* ignore */
        }
        throw new Error(message);
    }
    invalidateRoomKindOrder();
    return (await res.json()) as RoomKindOverridesResponse;
}

/** Clear one room kind's override, named for the page's property as a save is. */
export async function clearRoomKindOverride(roomKindId: string): Promise<void> {
    const res = await apiFetch(
        withPageConnection(`/api/room-kind-overrides/${encodeURIComponent(roomKindId)}`),
        { method: "DELETE" },
    );
    if (res.status === 409) throw await refusedWriteError(res, "clearRoomKindOverride failed");
    if (!res.ok) throw new Error(`clearRoomKindOverride failed: ${res.status}`);
    invalidateRoomKindOrder();
}
